import { ProductSelectEvent } from '@shopify/events';
import { morph } from '@theme/morph';
import { fetchSection } from '@theme/section-renderer';

class ProductInformation extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('click', this.onOptionClick, { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.requestController?.abort();
  }

  onOptionClick = (event) => {
    const option = event.target.closest('[data-option-value]');
    if (!(option instanceof HTMLAnchorElement)) return;
    if (option.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.updateProduct(option);
  };

  async updateProduct(option) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      window.location.assign(option.href);
      return;
    }

    const optionValueId = option.dataset.optionValueId || '';
    const connectedProductUrl = option.dataset.connectedProductUrl || '';
    const targetUrl = new URL(option.href, window.location.origin);
    this.requestController?.abort();
    this.requestController = new AbortController();
    const deferred = ProductSelectEvent.createPromise();

    this.dispatchEvent(new ProductSelectEvent({
      product: {
        id: this.dataset.productId || '',
        title: this.dataset.productTitle || '',
        handle: this.dataset.productHandle || '',
      },
      selectedOptions: this.getSelectedOptions(option),
      detail: {
        optionValueId,
        variantId: option.dataset.variantId || '',
        connectedProductUrl,
      },
      promise: deferred.promise,
    }));

    this.setAttribute('aria-busy', 'true');
    try {
      const documentResponse = await fetchSection(targetUrl, sectionId, this.requestController.signal);
      const nextRoot = documentResponse.querySelector('product-information');
      if (!(nextRoot instanceof HTMLElement)) throw new Error('Product section unavailable');

      const variantData = this.parseVariant(nextRoot);
      const nextProductId = nextRoot.dataset.productId || '';
      morph(this, nextRoot);
      history.replaceState({}, '', targetUrl);

      const selected = this.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`);
      if (selected instanceof HTMLElement) selected.focus({ preventScroll: true });

      const featuredMediaId = this.querySelector('[data-featured-media-id]')?.dataset.featuredMediaId;
      const gallery = this.querySelector('product-gallery');
      if (featuredMediaId && typeof gallery?.goToMedia === 'function') gallery.goToMedia(featuredMediaId);
      window.BabySupriseProductModel?.load?.();

      if (connectedProductUrl) {
        document.querySelector('product-recommendations')?.updateProduct?.(nextProductId);
      }

      deferred.resolve({
        variant: variantData,
        detail: {
          html: documentResponse,
          productId: nextProductId,
          sourceId: optionValueId,
          resource: null,
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (error.name !== 'AbortError') window.location.assign(targetUrl);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  parseVariant(root) {
    const script = root.querySelector('[data-selected-variant]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      const variant = JSON.parse(script.textContent);
      const selectedOptions = [...root.querySelectorAll('[data-option-value][aria-current="true"]')].map((option) => ({
        name: option.dataset.optionName || '',
        value: option.textContent.trim(),
      }));

      return {
        id: String(variant.id || ''),
        title: variant.title || '',
        availableForSale: Boolean(variant.available),
        price: {
          amount: (Number(variant.price || 0) / 100).toFixed(2),
          currencyCode: document.documentElement.dataset.currencyCode || '',
        },
        selectedOptions,
      };
    } catch {
      return null;
    }
  }

  getSelectedOptions(nextOption) {
    const selected = new Map();
    for (const option of this.querySelectorAll('[data-option-value][aria-current="true"]')) {
      selected.set(option.dataset.optionName || '', option.textContent.trim());
    }
    selected.set(nextOption.dataset.optionName || '', nextOption.textContent.trim());
    return [...selected].map(([name, value]) => ({ name, value }));
  }
}

if (!customElements.get('product-information')) customElements.define('product-information', ProductInformation);
